export * from '@wix/ambassador-innovation-widget-v2-widget-settings/types';
import {
  WidgetSettings as _WidgetSettings,
  ChatHandlerMode as _ChatHandlerMode,
  ChatHandler,
  ContactFormFields,
} from '@wix/ambassador-innovation-widget-v2-widget-settings/types';

export type WidgetSettings = _WidgetSettings;
export type ChatHandlerMode = _ChatHandlerMode;

export const getPagesWithChat = (settings?: WidgetSettings | null) =>
  settings?.commonBehavior?.pagesWithChat ?? [];

// WhatsApp's click-to-chat API expects `phone` as digits only, no formatting
// characters and no leading `+` — a distinct step from the client-side
// validation regex used in wix-ai-assistant-client-bm, which keeps the `+`.
export const buildWhatsAppUrl = ({
  phone,
  prefilledMessage,
}: {
  phone: string;
  prefilledMessage?: string;
}) => {
  const digitsOnlyPhone = phone.replace(/[\s()\-.+]/g, '');
  return (
    `https://api.whatsapp.com/send/?phone=${digitsOnlyPhone}` +
    `&text=${encodeURIComponent(prefilledMessage ?? '')}` +
    `&type=phone_number&app_absent=0`
  );
};

export const shouldShowChatOnPage = ({
  pageId,
  pagesWithChat,
}: {
  pageId?: string;
  pagesWithChat: string[];
}) => {
  if (pagesWithChat.length === 0) {
    return true;
  }

  if (!pageId) {
    return true;
  }

  return pagesWithChat.includes(pageId);
};

export type NormalizedContactFormFields = {
  [K in keyof ContactFormFields]-?: NonNullable<ContactFormFields[K]>;
};

const DEFAULT_CONTACT_FORM_FIELDS: NormalizedContactFormFields = {
  includeName: true,
  includeEmail: true,
  includePhone: true,
  includeMessage: true,
  requiredName: false,
  requiredEmail: true,
  requiredPhone: false,
  requiredMessage: false,
};

export const normalizeContactFormFields = (
  fields?: ContactFormFields,
): NormalizedContactFormFields => ({
  includeName: fields?.includeName ?? DEFAULT_CONTACT_FORM_FIELDS.includeName,
  includeEmail:
    fields?.includeEmail ?? DEFAULT_CONTACT_FORM_FIELDS.includeEmail,
  includePhone:
    fields?.includePhone ?? DEFAULT_CONTACT_FORM_FIELDS.includePhone,
  includeMessage:
    fields?.includeMessage ?? DEFAULT_CONTACT_FORM_FIELDS.includeMessage,
  requiredName:
    fields?.requiredName ?? DEFAULT_CONTACT_FORM_FIELDS.requiredName,
  requiredEmail:
    fields?.requiredEmail ?? DEFAULT_CONTACT_FORM_FIELDS.requiredEmail,
  requiredPhone:
    fields?.requiredPhone ?? DEFAULT_CONTACT_FORM_FIELDS.requiredPhone,
  requiredMessage:
    fields?.requiredMessage ?? DEFAULT_CONTACT_FORM_FIELDS.requiredMessage,
});

export const enum CurrentStatus {
  AI_ASSISTANT_ONLINE = 'AI_ASSISTANT_ONLINE',
  MANUAL_ONLINE = 'MANUAL_ONLINE',
  OFFLINE = 'OFFLINE',
}

export const getCurrentStatus = (handlers: ChatHandler[]) => {
  if (handlers?.length === 0) {
    return CurrentStatus.OFFLINE;
  } else if (handlers?.includes(ChatHandler.AI)) {
    return CurrentStatus.AI_ASSISTANT_ONLINE;
  } else if (handlers?.includes(ChatHandler.WIX_USER)) {
    return CurrentStatus.MANUAL_ONLINE;
  }
  return null;
};
